import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { Subject, forkJoin, takeUntil } from 'rxjs';
import { ToastService } from 'angular-toastify';
import { MapperTransactionParkingService } from '../../../../../core/mappers/parking/mapper-transaction-parking.service';
import { EditTransactionLicenseData, ParkingGateListResponse, ParkingTransactionResponse } from '../../../../../core/models/parking.model';
import { ApiFormService } from '../../../../../core/services/api-form.service';
import { ApiService } from '../../../../../core/services/api.service';
import { ApiTransactionParkingService } from '../../../../../core/services/api/parking/api-transaction-parking.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalForceExitTransactionComponent } from '../../../modals/parking/transaction-parking/modal-force-exit-transaction/modal-force-exit-transaction.component';
import { ModalTransactionParkingDetailComponent } from '../../../modals/parking/transaction-parking/modal-transaction-parking-detail/modal-transaction-parking-detail.component';
import { ModalTransactionParkingEditLicenseComponent } from '../../../modals/parking/transaction-parking/modal-transaction-parking-edit-license/modal-transaction-parking-edit-license.component';

@Component({
  selector: 'app-vehicles-in-parking',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PssLoadingComponent,
    ModalTransactionParkingDetailComponent,
    ModalTransactionParkingEditLicenseComponent,
    ModalForceExitTransactionComponent,
  ],
  templateUrl: './vehicles-in-parking.component.html',
  styleUrl: './vehicles-in-parking.component.css',
})
export class VehiclesInParkingComponent implements OnInit, OnDestroy {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiTransactionParkingService);
  private readonly apiForm = inject(ApiFormService);
  private readonly mapper = inject(MapperTransactionParkingService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly destroy$ = new Subject<void>();

  readonly transactions = signal<ParkingTransactionResponse[]>([]);
  readonly isLoading = signal(false);
  readonly totalItems = signal(0);
  readonly totalPages = signal(1);
  readonly lastFetchedTime = signal<Date | null>(null);

  userId = 0;
  currentPage = 1;
  pageSize = 20;
  readonly pageSizeOptions = [20, 50, 100];
  sortColumn = 'entryDate';
  sortDirection: 'asc' | 'desc' = 'asc';

  searchText = '';
  searchType: 'license_plate' | 'card_name' | 'gate_name' = 'license_plate';
  openDropdown: 'searchType' | 'pageSize' | null = null;
  showFilters = false;

  selectedGates: Record<string, boolean> = {};
  gateOptions: string[] = [];
  gateSearchText = '';
  selectedVehicleTypes: Record<string, boolean> = {};
  vehicleTypeOptions: string[] = [];

  enableLiveRefresh = false;
  private liveRefreshInterval: ReturnType<typeof setInterval> | null = null;

  showDetailModal = false;
  selectedParkingTransaction: ParkingTransactionResponse | null = null;
  showEditLicenseModal = false;
  editLicenseData: EditTransactionLicenseData = {
    userId: 0,
    oldLicense: '',
    transactionId: 0,
    entryPath1: '',
  };
  showImageModal = false;
  selectedImageUrl = '';

  constructor() {
    effect(() => {
      const siteId = this.siteState.selectedSite()?.id;
      if (!siteId) return;
      untracked(() => {
        this.currentPage = 1;
        if (this.userId > 0) this.loadTransactions();
      });
    });
  }

  /*#################################################### Function ################################################*/

  ngOnInit(): void {
    this.userId = Number(this.cookieService.get('user_id') || 0);
    this.loadVehicleTypeOptions();
    this.loadTransactions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearLiveRefreshInterval();
  }

  get currentSearchTypeLabel(): string {
    if (this.searchType === 'card_name') return 'บัตร/สมาชิก';
    if (this.searchType === 'gate_name') return 'จุดเข้า';
    return 'ทะเบียนรถ';
  }

  get filteredGateOptions(): string[] {
    const term = this.gateSearchText.trim().toLowerCase();
    return term
      ? this.gateOptions.filter((name) => name.toLowerCase().includes(term))
      : this.gateOptions;
  }

  get activeFilterCount(): number {
    return Object.keys(this.selectedGates).length + Object.keys(this.selectedVehicleTypes).length;
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    let start = Math.max(1, this.currentPage - 2);
    const end = Math.min(total, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }

  get startItemIndex(): number {
    return this.totalItems() === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems());
  }

  toggleDropdown(type: 'searchType' | 'pageSize'): void {
    this.openDropdown = this.openDropdown === type ? null : type;
  }

  closeDropdowns(): void {
    this.openDropdown = null;
  }

  selectSearchType(type: 'license_plate' | 'card_name' | 'gate_name'): void {
    this.searchType = type;
    this.openDropdown = null;
  }

  selectPageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.openDropdown = null;
    this.loadTransactions();
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadTransactions();
  }

  onRefresh(): void {
    this.currentPage = 1;
    this.loadTransactions();
  }

  clearFilters(): void {
    this.searchText = '';
    this.selectedGates = {};
    this.gateSearchText = '';
    this.selectedVehicleTypes = {};
    this.currentPage = 1;
    this.loadTransactions();
  }

  toggleGate(name: string): void {
    if (this.selectedGates[name]) delete this.selectedGates[name];
    else this.selectedGates[name] = true;
    this.currentPage = 1;
    this.loadTransactions();
  }

  toggleVehicleType(name: string): void {
    if (this.selectedVehicleTypes[name]) delete this.selectedVehicleTypes[name];
    else this.selectedVehicleTypes[name] = true;
    this.currentPage = 1;
    this.loadTransactions();
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.totalPages() || page === this.currentPage) return;
    this.currentPage = page;
    this.loadTransactions();
  }

  sortData(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.loadTransactions();
  }

  onLiveRefreshToggle(): void {
    if (this.enableLiveRefresh) this.startLiveRefreshInterval();
    else this.clearLiveRefreshInterval();
  }

  openDetailModal(item: ParkingTransactionResponse): void {
    this.selectedParkingTransaction = item;
    this.showDetailModal = true;
  }

  closeDetailModal(): void {
    this.showDetailModal = false;
    this.selectedParkingTransaction = null;
  }

  openEditLicenseModal(item: ParkingTransactionResponse): void {
    this.editLicenseData = {
      userId: this.userId,
      oldLicense: item.entryLicense || '',
      transactionId: item.id,
      entryPath1: item.entryPath1 || '',
    };
    this.showEditLicenseModal = true;
  }

  closeEditLicenseModal(): void {
    this.showEditLicenseModal = false;
  }

  openImageModal(url: string): void {
    this.selectedImageUrl = url || 'img/notfound.jpg';
    this.showImageModal = true;
  }

  closeImageModal(): void {
    this.showImageModal = false;
    this.selectedImageUrl = '';
  }

  entryImageUrl(item: ParkingTransactionResponse): string {
    return item.entryPath1
      ? `https://pss-upload.cloud/Parkingcaptures/${item.entryPath1}`
      : 'img/notfound.jpg';
  }

  formatDateDisplay(value: string | null): string {
    if (!value || value.startsWith('0001')) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  }

  parkingDuration(entryDate: string | null): string {
    if (!entryDate) return '-';
    const start = new Date(entryDate).getTime();
    if (Number.isNaN(start)) return '-';
    const minutes = Math.max(0, Math.floor((Date.now() - start) / 60000));
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    const remainingMinutes = minutes % 60;
    return days > 0
      ? `${days} วัน ${hours} ชม. ${remainingMinutes} นาที`
      : `${hours} ชม. ${remainingMinutes} นาที`;
  }

  getVehicleIcon(vehicleType: string | null): string {
    const type = (vehicleType || '').toLowerCase();
    return type.includes('motor') || type.includes('bike') || type.includes('มอเตอร์')
      ? 'two_wheeler'
      : 'directions_car';
  }

  private selectedNames(values: Record<string, boolean>): string | undefined {
    const names = Object.keys(values).filter((key) => values[key]);
    return names.length ? names.join(',') : undefined;
  }

  private startLiveRefreshInterval(): void {
    this.clearLiveRefreshInterval();
    this.liveRefreshInterval = setInterval(() => this.loadTransactions(true), 60000);
  }

  private clearLiveRefreshInterval(): void {
    if (!this.liveRefreshInterval) return;
    clearInterval(this.liveRefreshInterval);
    this.liveRefreshInterval = null;
  }

  /*############################################# API #################################################*/

  private loadVehicleTypeOptions(): void {
    this.apiForm.getParkingVehicleType().pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        console.log('getParkingVehicleType', response);
        this.vehicleTypeOptions = (response ?? [])
          .map((item) => ({ id: Number(item.id) || 0, name: (item.typeName || '').trim() }))
          .filter((item) => item.name)
          .sort((a, b) => a.id - b.id)
          .map((item) => item.name);
      },
      error: (error) => {
        console.error(error);
        this.toast.error('ไม่สามารถโหลดรายการประเภทรถได้');
      },
    });
  }

  loadTransactions(isSilent = false): void {
    const siteId = this.siteState.selectedSite()?.id ?? 0;
    if (!siteId || !this.userId) return;
    if (!isSilent) this.isLoading.set(true);

    this.api.getVehiclesInParkingForTable({
      siteId,
      userId: this.userId,
      page: this.currentPage,
      pageSize: this.pageSize,
      searchField: this.searchText.trim() ? this.searchType : undefined,
      searchText: this.searchText.trim() || undefined,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
      gateNames: this.selectedNames(this.selectedGates),
      vehicleTypeNames: this.selectedNames(this.selectedVehicleTypes),
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        console.log('getVehiclesInParkingForTable', response);
        const mapped = this.mapper.mapTableFromDto(response);
        this.transactions.set(mapped.items);
        this.pruneSelections();
        this.totalItems.set(mapped.total);
        this.totalPages.set(mapped.totalPages || 1);
        if (mapped.gateOptions.length) this.gateOptions = mapped.gateOptions;
        if (!this.vehicleTypeOptions.length && mapped.vehicleTypeOptions.length) {
          this.vehicleTypeOptions = mapped.vehicleTypeOptions;
        }
        this.lastFetchedTime.set(new Date());
        if (!isSilent) this.isLoading.set(false);
      },
      error: (error) => {
        console.error(error);
        if (!isSilent) {
          this.isLoading.set(false);
          this.toast.error('ไม่สามารถโหลดข้อมูลรถค้างในลานได้');
        }
      },
    });
  }

  // --- Clear รถค้าง: ทำรายการออกรายคัน / หลายคัน (เหมือนหน้า Transaction) ---
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly legacyApiService = inject(ApiService);

  showForceExitModal = false;
  selectedForceExitTransaction: ParkingTransactionResponse | null = null;

  selectedTransactions = new Set<number>();
  showBulkCloseModal = false;
  bulkRemark = '';
  isBulkClosing = false;
  /** ปิดใน DB อย่างเดียว ไม่ส่ง Taiwan — กรณี Taiwan ปิดไปแล้วแต่ไม่ได้แจ้งกลับมา */
  bulkLocalOnly = false;

  bulkExitGates: ParkingGateListResponse[] = [];
  bulkExitGateId = 0;
  bulkGateSearchText = '';
  isBulkGateDropdownOpen = false;

  get canEdit(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get isAllSelected(): boolean {
    const rows = this.transactions();
    return rows.length > 0 && rows.every(t => this.selectedTransactions.has(t.id));
  }

  toggleSelection(id: number): void {
    if (this.selectedTransactions.has(id)) this.selectedTransactions.delete(id);
    else this.selectedTransactions.add(id);
  }

  toggleSelectAll(): void {
    if (this.isAllSelected) {
      this.selectedTransactions.clear();
    } else {
      this.transactions().forEach(t => this.selectedTransactions.add(t.id));
    }
  }

  openForceExitModal(item: ParkingTransactionResponse): void {
    if (!this.canEdit) {
      this.toast.error('ไม่มีสิทธิ์ทำรายการออก');
      return;
    }
    this.selectedForceExitTransaction = item;
    this.showForceExitModal = true;
  }

  closeForceExitModal(): void {
    this.showForceExitModal = false;
    this.selectedForceExitTransaction = null;
  }

  onForceExitSuccess(): void {
    this.loadTransactions(true);
  }

  openBulkCloseModal(): void {
    if (this.selectedTransactions.size === 0) return;
    if (!this.canEdit) {
      this.toast.error('ไม่มีสิทธิ์ทำรายการออก');
      return;
    }
    this.bulkRemark = '';
    this.bulkLocalOnly = false;
    this.bulkExitGateId = 0;
    this.bulkGateSearchText = '';
    this.isBulkGateDropdownOpen = false;
    this.loadBulkExitGates();
    this.showBulkCloseModal = true;
  }

  closeBulkCloseModal(): void {
    if (this.isBulkClosing) return;
    this.showBulkCloseModal = false;
    this.bulkRemark = '';
    this.isBulkGateDropdownOpen = false;
  }

  get filteredBulkExitGates(): ParkingGateListResponse[] {
    const q = this.bulkGateSearchText.trim().toLowerCase();
    const selectedName = this.selectedBulkExitGateName.toLowerCase();
    if (!q || q === selectedName) return this.bulkExitGates;
    return this.bulkExitGates.filter(g => g.name.toLowerCase().includes(q));
  }

  get selectedBulkExitGateName(): string {
    return this.bulkExitGates.find(g => g.id === this.bulkExitGateId)?.name ?? '';
  }

  selectBulkExitGate(gate: ParkingGateListResponse): void {
    this.bulkExitGateId = gate.id;
    this.bulkGateSearchText = gate.name;
    this.isBulkGateDropdownOpen = false;
  }

  closeBulkGateDropdown(): void {
    this.isBulkGateDropdownOpen = false;
    this.bulkGateSearchText = this.selectedBulkExitGateName;
  }

  confirmBulkClose(): void {
    const remark = this.bulkRemark.trim();
    if (!remark) {
      this.toast.error('กรุณาระบุเหตุผลในการทำรายการออก');
      return;
    }
    const menuId = this.permissionService.getMenuId(this.router.url);
    if (!menuId) {
      this.toast.error('ไม่พบ Menu ID');
      return;
    }
    if (!this.bulkLocalOnly && this.bulkExitGates.length > 0 && this.bulkExitGateId <= 0) {
      this.toast.error('กรุณาเลือกประตูออก');
      return;
    }

    this.isBulkClosing = true;
    const payload = {
      userId: this.userId,
      remark,
      menuId,
      parkingGateId: this.bulkLocalOnly ? 0 : this.bulkExitGateId,
      localOnly: this.bulkLocalOnly,
    };
    const ids = Array.from(this.selectedTransactions);
    const requests = ids.map(id => this.legacyApiService.closingTransaction(payload, id));

    forkJoin(requests).pipe(takeUntil(this.destroy$)).subscribe({
      next: (results) => {
        this.isBulkClosing = false;
        const failures = results
          .map((r, i) => ({ r, id: ids[i] }))
          .filter(x => !x.r?.isSuccess);
        const successes = ids.length - failures.length;
        if (successes > 0) {
          this.toast.success(`ทำรายการสำเร็จ ${successes}/${ids.length} รายการ`);
        }
        if (failures.length > 0) {
          const plateOf = (id: number) =>
            this.transactions().find(t => t.id === id)?.entryLicense || `#${id}`;
          const detail = failures
            .slice(0, 3)
            .map(x => `${plateOf(x.id)}: ${x.r?.message || 'ไม่สำเร็จ'}`)
            .join(' | ');
          const more = failures.length > 3 ? ` และอีก ${failures.length - 3} รายการ` : '';
          this.toast.error(`ไม่สำเร็จ ${failures.length} รายการ — ${detail}${more}`);
        }
        this.selectedTransactions.clear();
        this.showBulkCloseModal = false;
        this.bulkRemark = '';
        this.loadTransactions(true);
      },
      error: (err) => {
        console.error(err);
        this.isBulkClosing = false;
        this.toast.error('เกิดข้อผิดพลาดในการทำรายการบางส่วน');
        this.loadTransactions(true);
      },
    });
  }

  private loadBulkExitGates(): void {
    this.bulkExitGates = [];
    if (this.selectedSiteId <= 0) return;
    this.legacyApiService.getParkingGate(this.selectedSiteId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.bulkExitGates = (res ?? []).filter(g => {
          const raw = g as ParkingGateListResponse & { Exit?: boolean };
          return g.exit === true || raw.Exit === true;
        });
        if (this.bulkExitGates.length === 1) {
          this.selectBulkExitGate(this.bulkExitGates[0]);
        }
      },
      error: () => (this.bulkExitGates = []),
    });
  }

  /** ปิด dropdown เมื่อคลิกนอก — ใช้แทน overlay fixed ที่ถูก card (transform จาก animation) บังคลิก */
  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.openDropdown) this.closeDropdowns();
  }

  /** เลือกได้เฉพาะแถวที่อยู่ในหน้าปัจจุบัน (รายการที่ปิดแล้วจะหายจาก list) */
  private pruneSelections(): void {
    if (this.selectedTransactions.size === 0) return;
    const visibleIds = new Set(this.transactions().map(t => t.id));
    this.selectedTransactions.forEach(id => {
      if (!visibleIds.has(id)) this.selectedTransactions.delete(id);
    });
  }
}
